// 오늘의 브리핑 — deterministic 인사이트 (P0, 2026-09-17). LLM 없음.
// analytics 원시 값(overview today/yesterday + timeseries 7일 + tickets)만으로
// "무엇이 달라졌고 / 무엇을 주의하고 / 무엇을 확인할지"를 계산한다. 순수 함수(React 밖).
//
// 원칙:
//  · source of truth = 이 계산 결과. 원인 추측 금지(계산된 사실만).
//  · 0 분모/데이터 없음 = null/noBaseline 로 안전 처리(Infinity/NaN 금지).
//  · 작은 절대값에서 %가 튀는 문제를 noise floor 로 차단.
//  · threshold/noise floor 는 아래 상수 한 곳에서만 관리(추후 실데이터로 튜닝).
//
// ⚠ 시간축 정확성 (2026-09-17 temporal audit):
//   overview 는 KST 일 단위 집계만 제공한다(datetime cutoff 없음). 그래서 today 는
//   00:00~now(부분 누적), yesterday 는 완결일(00:00~23:59) — 누적형 지표의 today vs
//   yesterday/7일평균 비교는 "부분일 vs 완결일"이라 daytime 에 항상 낮게 나와 잘못된
//   감소/증가 alert 를 만든다. same-time 비교를 위한 새 backend API 는 만들지 않으므로,
//   P0 signal 은 "현재 시점에서도 유효한 state/ratio"만 유지한다:
//     유지: margin_negative(현재 누적 적자), tickets_open(미처리 건수)
//     비활성(P1, backend hourly same-time 지원 후): cost_spike, request_drop/surge,
//       active_users_drop, margin_pct_drop
//   changePctYd/changePct7d 는 summary 에 계산은 하되(UI 참고용) signal source 로 쓰지
//   않는다. UI 도 "전일 대비"로 오해시키지 않는다(핵심 변화 카드는 '오늘 누적'으로 표기).
//   avg7d 는 D-1~D-7(오늘 제외) baseline — 원칙상 올바르나 완결일 평균이라 부분일과의
//   비교는 여전히 same-time 아님(위와 동일 이유로 signal 미사용).

// ── threshold / noise floor (추후 staging 실분포로 튜닝) ─────────────────────
export const BRIEFING_THRESHOLDS = {
  requestDeviationPct: 25,     // 오늘 vs 7일평균 ±25%
  costSpikePct: 25,            // AI 원가 오늘 vs 어제 +25%
  marginPctDropPp: 5,          // 기여이익률 어제 대비 -5%p
  activeUsersDropPct: 25,      // 활성 사용자 오늘 vs 7일평균 -25%
  noiseFloor: {
    requestsAvg7d: 50,         // 7일평균 호출 50건 미만이면 request signal 억제
    activeUsersAvg7d: 5,       // 7일평균 활성 5명 미만이면 active signal 억제
    aiCostKrw: 10_000,         // 원가 1만원 미만이면 cost_spike 억제(소액 % 튐 방지)
  },
};

const DEEP_LINKS = {
  finops: "/dashboard?tab=finops",
  trend: "/dashboard?tab=trend",
  usage: "/dashboard?tab=toolUsage",
  tickets: "/tickets",
};

// ── 순수 헬퍼 ──────────────────────────────────────────────────────────────
// 변화율(%) — baseline 0/누락이면 null(신규/기준없음). Infinity/NaN 절대 반환 안 함.
export function changePct(current, baseline) {
  if (typeof current !== "number" || typeof baseline !== "number") return null;
  if (baseline === 0) return null; // new / noBaseline — %는 무의미
  return ((current - baseline) / Math.abs(baseline)) * 100;
}

function num(v) { return typeof v === "number" && Number.isFinite(v) ? v : null; }

// ── P1A same-time (동시간) 비교 ──────────────────────────────────────────────
// hourly series(bucket "YYYY-MM-DDTHH", KST)에서 각 날짜의 00시~cutoff시 누적을 만든다.
// cutoff = 현재 KST hour - 1 (진행 중 hour 제외 = 완료된 마지막 hour). baseline 오염 방지.
// 반환: { byDate: {date: {requests, revenueKrw, usdCost, userIds:Set}}, cutoffHour }
export function aggregateSameTime(hourlySeries, nowKstHour) {
  const cutoffHour = (typeof nowKstHour === "number" ? nowKstHour : 0) - 1;
  const byDate = {};
  if (cutoffHour < 0) return { byDate, cutoffHour }; // 완료된 hour 없음(자정 직후)
  for (const row of hourlySeries || []) {
    const bucket = row && row.bucket;
    if (typeof bucket !== "string" || bucket.length < 13) continue;
    const date = bucket.slice(0, 10);
    const hour = parseInt(bucket.slice(11, 13), 10);
    if (!Number.isInteger(hour) || hour > cutoffHour) continue; // 동시간 window 밖 제외
    const d = byDate[date] || (byDate[date] = { requests: 0, revenueKrw: 0, usdCost: 0, userIds: new Set() });
    d.requests += num(row.requestCount) || 0;
    d.revenueKrw += num(row.revenueKrw) || 0;
    d.usdCost += num(row.usdCost) || 0;
    for (const u of (Array.isArray(row.activeUserIds) ? row.activeUserIds : [])) if (u) d.userIds.add(u);
  }
  return { byDate, cutoffHour };
}

// same-time 요약 — today/yesterday/7일평균(D-2~D-7, 오늘·어제 제외 아님: 어제 포함 최근 완료일들).
// baseline 이 없으면(hour 없음/completed day 부족) null 로 두어 signal 미복원.
function sameTimeSummary(hourlySeries, todayDate, nowKstHour) {
  const { byDate, cutoffHour } = aggregateSameTime(hourlySeries, nowKstHour);
  if (cutoffHour < 0) return null; // 진행 중 hour 만 있음 → 비교 불가
  const dates = Object.keys(byDate).sort();
  const pastDates = dates.filter((d) => d < todayDate);
  if (pastDates.length === 0) return null; // 과거 비교군 부재 → 판단 불가
  // today 버킷이 없어도(오늘 활동 0) 과거 baseline 은 유효 — zero-value 로 취급해 비교 유지.
  // (backend briefing_evidence._same_time_summary 와 parity)
  const today = byDate[todayDate] || { requests: 0, revenueKrw: 0, usdCost: 0, userIds: new Set() };

  const yDate = pastDates[pastDates.length - 1];
  const yesterday = byDate[yDate];
  // 최근 7일(오늘 제외) 동시간 평균 — pastDates 중 최근 7개.
  const recent = pastDates.slice(-7).map((d) => byDate[d]);
  const avgOf = (key) => recent.length ? recent.reduce((s, r) => s + r[key], 0) / recent.length : null;
  const avgUsers = recent.length ? recent.reduce((s, r) => s + r.userIds.size, 0) / recent.length : null;

  return {
    cutoffHour,
    baselineDays: recent.length,
    requests: { today: today.requests, yesterday: yesterday.requests, avg7d: avgOf("requests") },
    revenueKrw: { today: today.revenueKrw, yesterday: yesterday.revenueKrw },
    usdCost: { today: today.usdCost, yesterday: yesterday.usdCost, avg7d: avgOf("usdCost") },
    activeUsers: { today: today.userIds.size, yesterday: yesterday.userIds.size, avg7d: avgUsers },
    // marginPct = (revenue - costKrw)/revenue. cost 는 usd → krw 환산 필요(정확 환율은 backend 소유).
    // 여기선 usdCost 만 있으므로 marginPct 동시간 복원은 revenue 대비 usdCost 비율로 근사하지 않고,
    // 정확 환산 상수를 프론트가 소유하지 않아 same-time marginPct 는 복원하지 않는다(아래 판단).
  };
}

function avg(nums) {
  const xs = nums.filter((n) => typeof n === "number" && Number.isFinite(n));
  if (xs.length === 0) return null;
  return xs.reduce((a, b) => a + b, 0) / xs.length;
}

// metric 비교 객체 — today/yesterday/avg7d + 각 변화율. 값 없으면 null.
function metric(today, yesterday, avg7d) {
  return {
    today: num(today),
    yesterday: num(yesterday),
    avg7d: num(avg7d),
    changePctYd: changePct(num(today), num(yesterday)),
    changePct7d: changePct(num(today), num(avg7d)),
  };
}

// ── 메인: insight 생성 ──────────────────────────────────────────────────────
// 입력(모두 optional — 없으면 해당 비교 생략, briefing 은 계속 동작):
//   today, yesterday : overview.kpi (일 단위, state signal 용)
//   sevenDaySeries   : timeseries.series (day) — 참고용 avg7d(비교 아님)
//   hourlySeries     : timeseries.series (hour, D-7~today) — P1A same-time 비교의 정본
//   nowKstHour       : 현재 KST 시(정수 0~23). cutoff = nowKstHour-1(진행 중 hour 제외)
//   todayDate        : "YYYY-MM-DD" (KST 오늘) — hourly byDate 매칭용
//   openTicketsToday/Yesterday : number | null
export function buildBriefingInsight({ today, yesterday, sevenDaySeries, hourlySeries, nowKstHour, todayDate, openTicketsToday, openTicketsYesterday } = {}) {
  const t = today || {};
  const y = yesterday || {};
  const series = Array.isArray(sevenDaySeries) ? sevenDaySeries : [];

  const avg7dRequests = series.length ? avg(series.map((r) => num(r.requestCount))) : null;
  const avg7dActive = series.length ? avg(series.map((r) => num(r.activeUsers))) : null;

  const summary = {
    requests:    metric(t.requestCount, y.requestCount, avg7dRequests),
    revenueKrw:  metric(t.revenueKrw, y.revenueKrw, null),
    aiCostKrw:   metric(t.bedrockCostKrw, y.bedrockCostKrw, null),
    marginKrw:   metric(t.businessMarginKrw, y.businessMarginKrw, null),
    marginPct:   marginPctMetric(t.businessMarginPct, y.businessMarginPct),
    activeUsers: metric(t.uniqueUsers, y.uniqueUsers, avg7dActive),
    openTickets: { today: num(openTicketsToday), yesterday: num(openTicketsYesterday) },
  };

  // P1A: same-time(동시간) 비교 — hourly 정본. 없으면 null → 누적형 signal 미복원.
  const sameTime = (Array.isArray(hourlySeries) && typeof nowKstHour === "number" && todayDate)
    ? sameTimeSummary(hourlySeries, todayDate, nowKstHour)
    : null;

  const signals = buildSignals(summary, sameTime);
  const actions = buildActions(signals);
  const headline = buildHeadline(signals);

  return { summary, sameTime, signals, actions, headline };
}

// 기여이익률은 %p(포인트) 차이가 의미 — 별도 metric.
function marginPctMetric(today, yesterday) {
  const tt = num(today), yy = num(yesterday);
  return {
    today: tt,
    yesterday: yy,
    deltaPp: tt != null && yy != null ? tt - yy : null,
  };
}

const SEV_ORDER = { critical: 0, warning: 1, info: 2 };

function buildSignals(s, sameTime) {
  const T = BRIEFING_THRESHOLDS;
  const out = [];

  // ── state/ratio signal (시점 무관, 항상 유효) ──
  if (s.marginKrw.today != null && s.marginKrw.today < 0) {
    out.push({
      type: "margin_negative", severity: "critical", metric: "marginKrw",
      title: "직접비 기여이익 적자",
      detail: "오늘 현재까지 직접비 기여이익이 적자입니다.",
      currentValue: s.marginKrw.today, baselineValue: null, change: null,
      deepLink: DEEP_LINKS.finops,
    });
  }
  if (s.openTickets.today != null && s.openTickets.today > 0) {
    out.push({
      type: "tickets_open", severity: s.openTickets.today >= 3 ? "critical" : "warning", metric: "openTickets",
      title: "미처리 오류 제보",
      detail: `미처리 오류 제보 ${s.openTickets.today}건이 있습니다.`,
      currentValue: s.openTickets.today, baselineValue: s.openTickets.yesterday, change: null,
      deepLink: DEEP_LINKS.tickets,
    });
  }

  // ── P1A: same-time(동시간) 누적형 signal 복원 ──
  // sameTime 이 있을 때만(hourly 정본 + 완료 hour 존재). "부분일 vs 완결일" 오판을 제거해
  // 오늘 00~cutoff 시 vs 과거 같은 시간대 window 로 비교한다. cutoff 시각을 문구에 명시.
  if (sameTime) {
    const H = String(sameTime.cutoffHour).padStart(2, "0");
    const win = `오늘 00~${H}시`;

    // cost_spike — same-time usdCost 어제 동시간 대비 +threshold (noise floor: usdCost 최소)
    {
      const c = sameTime.usdCost;
      const pct = changePct(c.today, c.yesterday);
      // usd noise floor: aiCostKrw floor(원) 를 대략 usd 로 환산(1300원/$ 가정) — 정확 환율은
      // backend 소유라 프론트는 보수적 하한만 둔다(소액 % 튐 방지 목적).
      const usdFloor = T.noiseFloor.aiCostKrw / 1300;
      if (c.today != null && c.today >= usdFloor && pct != null && pct >= T.costSpikePct) {
        out.push({
          type: "cost_spike", severity: "warning", metric: "aiCost",
          title: "AI 원가 급증",
          detail: `AI 원가가 어제 동시간(${win}) 대비 ${Math.round(pct)}% 증가했습니다.`,
          currentValue: c.today, baselineValue: c.yesterday, change: pct,
          deepLink: DEEP_LINKS.finops,
        });
      }
    }

    // request_drop / surge — same-time 7일 동시간 평균 대비 (noise floor: avg7d 요청)
    {
      const r = sameTime.requests;
      const pct7 = changePct(r.today, r.avg7d);
      const floorOk = r.avg7d != null && r.avg7d >= T.noiseFloor.requestsAvg7d;
      if (floorOk && pct7 != null) {
        if (pct7 <= -T.requestDeviationPct) {
          out.push({
            type: "request_drop", severity: "warning", metric: "requests",
            title: "호출량 감소",
            detail: `오늘 호출량이 최근 7일 동시간 평균(${win}) 대비 ${Math.round(Math.abs(pct7))}% 낮습니다.`,
            currentValue: r.today, baselineValue: r.avg7d, change: pct7, deepLink: DEEP_LINKS.usage,
          });
        } else if (pct7 >= T.requestDeviationPct) {
          out.push({
            type: "request_surge", severity: "info", metric: "requests",
            title: "호출량 급증",
            detail: `오늘 호출량이 최근 7일 동시간 평균(${win}) 대비 ${Math.round(pct7)}% 높습니다.`,
            currentValue: r.today, baselineValue: r.avg7d, change: pct7, deepLink: DEEP_LINKS.usage,
          });
        }
      }
    }

    // active_users_drop — same-time 7일 동시간 평균 대비 (activeUserIds union 기반, noise floor)
    {
      const a = sameTime.activeUsers;
      const pct7 = changePct(a.today, a.avg7d);
      const floorOk = a.avg7d != null && a.avg7d >= T.noiseFloor.activeUsersAvg7d;
      if (floorOk && pct7 != null && pct7 <= -T.activeUsersDropPct) {
        out.push({
          type: "active_users_drop", severity: "warning", metric: "activeUsers",
          title: "활성 사용자 감소",
          detail: `활성 사용자가 최근 7일 동시간 평균(${win}) 대비 ${Math.round(Math.abs(pct7))}% 적습니다.`,
          currentValue: a.today, baselineValue: a.avg7d, change: pct7, deepLink: DEEP_LINKS.usage,
        });
      }
    }
    // margin_pct_drop 은 same-time 에서도 복원하지 않는다 — hourly 에 AI 원가가 usd 만 있고
    // 정확한 KRW 환산 상수를 프론트가 소유하지 않아 marginPct 를 신뢰 있게 재계산할 수 없다.
    // (P1B/backend 에서 hourly bedrockCostKrw 제공 시 복원.)
  }

  out.sort((x, z) => {
    const sd = SEV_ORDER[x.severity] - SEV_ORDER[z.severity];
    if (sd !== 0) return sd;
    return Math.abs(z.change || 0) - Math.abs(x.change || 0);
  });
  return out;
}

// 액션 — signal 에서 생성, 같은 deepLink 는 합침, 최대 3개.
function buildActions(signals) {
  const seen = new Set();
  const actions = [];
  for (const sig of signals) {
    if (seen.has(sig.deepLink)) continue; // 같은 화면 중복 제거
    seen.add(sig.deepLink);
    actions.push({ priority: actions.length + 1, text: sig.detail, deepLink: sig.deepLink, severity: sig.severity });
    if (actions.length >= 3) break;
  }
  return actions;
}

// headline — deterministic template. critical>warning>정상. 과장 금지, 최대 1~2 사실.
function buildHeadline(signals) {
  if (signals.length === 0) return "현재 주요 운영 지표에 큰 이상 징후가 없습니다.";
  const crit = signals.filter((s) => s.severity === "critical");
  const warn = signals.filter((s) => s.severity === "warning");
  const pick = (crit.length ? crit : warn.length ? warn : signals).slice(0, 2);
  // 두 사실을 자연스럽게 연결.
  const parts = pick.map((s) => s.detail.replace(/\.$/, ""));
  return parts.join(", ") + ".";
}
